import Accordion from "components/blocks/Accordion";
import AccordionItem from "components/blocks/Accordion/AccordionItem";
import FilePreview from "components/blocks/FileInput/FilePreview";
import FileUploadWithPreview, {SelectedFilesType} from "components/blocks/FileUploadWithPreview";
import {Dispatch, SetStateAction, useEffect, useMemo, useRef, useState} from "react";
import {FileResult, FileUploadResponse} from "services/shared-models/sharedTypes";
import {
    OrderLocalItemResult,
    ProgramOrderIterationResult,
    ProgramOrderLocalResult
} from "services/orders/models/OrdersTypes";
import {useTranslation} from "react-i18next";
import PaginatedLookupDropdown from "components/blocks/PaginatedLookupDropdown";
import {OrdersService} from "services/orders";
import {useNotification} from "hooks/useNotification";
import LoadingOverlay from "components/blocks/LoadingOverlay";
import Grid from "components/GridView/components/Grid";
import { GridSchema } from "components/GridView/GridView.types";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import ExaminersTable from "./ExaminersTable";
import EvaluationFormPreviewModal from "views/home/evaluationForms/EvaluationFormPreviewModal";
import Modal from "../../../../../../../../components/blocks/Modal";
import AttachmentCard from "../../../../../../../../components/blocks/AttachmentCard";
import IconButton from "../../../../../../../../components/blocks/IconButton";

export type ExaminerSelectedTemplateType = {
    title: string;
    id: number;
    currentIteration?: number
};

const ExaminersSection = ({
                              order,
                              setTriggerOrderUpdate,
                              currentOrder,
                              orderFiles,
                              isReadonly
                          }: {
    order: ProgramOrderIterationResult | null;
    currentOrder?: OrderLocalItemResult | null;
    orderFiles?: OrderLocalItemResult["files"];
    setTriggerOrderUpdate?: Dispatch<SetStateAction<boolean>>;
    isReadonly: boolean;
}) => {
    const {t} = useTranslation(["Orders"]);
    const {showNotification} = useNotification();
    const { toSystemThemeDateFormat } = useSystemDateTheme();

    const [evaluationTemplateModalVisible, setEvaluationTemplateModalVisible] = useState(false);
    const [updatingProgramEvaluationOrderIsLoading, setUpdatingProgramEvaluationOrderIsLoading] = useState(false);
    const [files, setFiles] = useState<SelectedFilesType[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<ExaminerSelectedTemplateType>({
        title: "",
        id: order?.evaluationFormId ?? 0,
        currentIteration: order?.iteration ?? 0,
    });
    const [historyModal, setHistoryModal] = useState<{ isOpen: boolean; files?: FileResult[] }>({ isOpen: false, files: [] });

    useEffect(() => {
        if (order) {
            setSelectedTemplate({
                title: order?.evaluationForm?.evaluationFormDetail.displayName ?? t("Common:noData"),
                id: order?.evaluationFormId ?? 0,
                currentIteration: order?.iteration ?? 0
            });

            const filesIdsSet = new Set();
            const uniqueFiles = [...(order.files ?? [])].reduce((acc, file) => {
                if (!filesIdsSet.has(file.id)) {
                    acc.push({
                        file: new File([], file.name ?? ""),
                        uuid: file.id,
                        fileName: file.name ?? "",
                        size: file.size,
                        showDeleteButton: true,
                        createdOn: file.createdOn,
                        createdBy: file.createdBy,
                    });
                    filesIdsSet.add(file.id);
                }
                return acc;
            }, [] as SelectedFilesType[]);

            setFiles(uniqueFiles);
        }
    }, [order]);

    const historyFileIds = useMemo(() => {
        const ids = new Set<string>();
        const histories = order?.programOrderIterationFileHistories ?? [];
        histories.forEach((h: any) => {
            const hFiles = h?.files ?? [];
            hFiles.forEach((f: FileResult) => {
                if (f?.id) ids.add(f.id);
            });
        });
        return ids;
    }, [order]);

    const modifiedHistories = useMemo(() => {
        console.log(order?.programOrderIterationFileHistories);
        const all = order?.programOrderIterationFileHistories ?? [];
        return all.filter((h: any) => h.actionType === "Updated");
    }, [order]);

    const modifiedHistoryGridSchema = useMemo<GridSchema[]>(() => {
        return [
            {
                field: "TheUpdate",
                displayName: t("TheUpdate", { ns: "Orders" }) ,
                render: (row: any,index:number) =>t("Update", { ns: "Orders" }) + ` ${index + 1}` ,
            },
            {
                field: "modifiedOn",
                displayName: t("modifiedDate", { ns: "Orders" }),
                render: (row: any) => toSystemThemeDateFormat(row?.createdOn ?? row?.modifiedOn ?? row?.date),
            },
          {
            field: "attachments",
            displayName: t("Common:attachments"),
            showOnMobile: true,
            render: (row: any) => (
              <span className="d-flex justify-content-center">
                <IconButton
                  fitToIconWidth
                  icon="icon-eye"
                  innerIconColor="primary"
                  size="md"
                  onClick={() => {
                    const files = (row?.files as FileResult[]) ?? (row?.file ? [row.file] : []);
                    setHistoryModal({ isOpen: true, files: files ?? [] });
                  }}
                />
              </span>
            ),
          }
         
        ];
    }, [t, toSystemThemeDateFormat]);

    
    
    const updateProgramIterationOrder = async ({
                                                   evaluationFormId,
                                                   filesIds,
                                               }: {
        evaluationFormId: number;
        filesIds: string[];
    }) => {
        if (!order) return;

        try {
            setUpdatingProgramEvaluationOrderIsLoading(true);
            const response = await OrdersService.updateProgramIterationOrder(order.id, {
                evaluationFormId: evaluationFormId,
                filesIds: filesIds,
            });
            if (response.hasError) {
                showNotification({
                    description: t(response.description),
                    type: "error",
                });
            } else {
                showNotification({
                    description: t("Common:success"),
                    type: "success",
                });
            }
        } catch {
            showNotification({
                description: t("Common:internalServerError"),
                type: "error",
            });
        } finally {
            setUpdatingProgramEvaluationOrderIsLoading(false);
        }
    };

    return (
        <>

            <div className="d-flex flex-column gap-4 border-bottom pb-4">
                <div className="d-flex flex-column gap-4 position-relative">
                    <LoadingOverlay visible={updatingProgramEvaluationOrderIsLoading}/>
                    <PaginatedLookupDropdown
                        label={t("evaluationTemplate")}
                        service="evaluationFormsService"
                        endpoint="getAllEvaluationForms"
                        disabled={isReadonly}
                        queryKey="search"
                        idValueKey="id"
                        textValueKey="evaluationFormDetail.displayName"
                        customFilter={[{field: "Status", value: "Active", operator: "equal"}]}
                        isPaginated
                        value={selectedTemplate?.id}
                        onChange={(id, item) => {
                            setSelectedTemplate({
                                title: item.label,
                                id: +id,
                            });

                            updateProgramIterationOrder({
                                evaluationFormId: +id,
                                filesIds: files.map((file) => file.uuid),
                            });
                        }}
                    />

                    {selectedTemplate && (
                        <FilePreview
                            disabled={isReadonly}
                            title={selectedTemplate.title}
                            callbackValue={selectedTemplate.id}
                            onShow={() => {
                                setEvaluationTemplateModalVisible(true);
                            }}
                            showFileSize={false}
                            hideDeleteButton
                        />
                    )}

                    {selectedTemplate && (
                        <FileUploadWithPreview
                            label={t("EvaluationsFileIterationLabel")}
                            files={files}
                            uploadWithUser={true}
                            isDisabled={isReadonly}
                            setFiles={setFiles}
                            newFiles={[]}
                            onChange={async (files) => {
                                if (selectedTemplate)
                                    await updateProgramIterationOrder({
                                        evaluationFormId: selectedTemplate.id,
                                        filesIds: files.map((file) => file.uuid),
                                    });
                            }}
                            onDelete={async (files) => {
                                if (selectedTemplate)
                                    await updateProgramIterationOrder({
                                        evaluationFormId: selectedTemplate.id,
                                        filesIds: files.map((file) => file.uuid),
                                    });
                            }}
                        />
                    )}
                </div>
                <div className="pb-4">
                    <h6 className="fw-bold">{ t("EvaluationFiles")}</h6>
                    <div className="d-flex gap-3 mt-2 ">
                        {orderFiles?.filter((f) => !historyFileIds.has(f.id)).length ? (
                            orderFiles?.filter((f) => !historyFileIds.has(f.id)).map((attachment) => (
                                <AttachmentCard
                                    key={attachment.id}
                                    attachment={{
                                        id: attachment.id,
                                        fileName: attachment.name ?? "",
                                        size: attachment.size ?? "",
                                        extension: attachment.extension ?? "",
                                    }}
                                />
                            ))
                        ) : (
                            <span>{t("Common:noData")}</span>
                        )}
                    </div>
                </div>
            

                <ExaminersTable
                    orderId={order?.id ?? 0}
                    fundingUnitNotes={""}
                    programOrderEvaluationId={order?.id ?? 0}
                    selectedTemplate={selectedTemplate}
                    setTriggerOrderUpdate={() => {
                        if (setTriggerOrderUpdate) {
                            setTriggerOrderUpdate((prev) => !prev);
                        }
                    }}
                    setSelectedTemplate={setSelectedTemplate}
                    isReadonly={isReadonly}
                />
                <div className="pb-4">
                    <h6 className="fw-bold">{ t("HistoryFiles")}</h6>
                <div className="mt-4">
                  <Grid gridSchema={modifiedHistoryGridSchema} data={modifiedHistories} />
                </div>
                </div>

                <Modal
                  containerClassName="bg-white pb-2"
                  isOpen={historyModal.isOpen}
                  onClose={() => setHistoryModal({ isOpen: false, files: [] })}
                  title={t("Common:attachments")}
                  withHeaderBorder
                  size="lg"
                  titleClassName="fs-6 text-dark"
                >
                  <div className="d-flex gap-2 flex-wrap">
                    {historyModal.files && historyModal.files.length > 0 ? (
                      historyModal.files.map((file: FileResult, index: number) => (
                        <AttachmentCard
                          key={`${file.id ?? index}`}
                          attachment={{
                            extension: file.extension ?? "",
                            fileName: file.name ?? "",
                            id: file.id ?? "",
                            size: file.size ?? "",
                          }}
                        />
                      ))
                    ) : (
                      t("Common:noData")
                    )}
                  </div>
                </Modal>
            </div>


            <EvaluationFormPreviewModal
                evaluationFormId={selectedTemplate.id}
                title={selectedTemplate.title}
                isOpen={evaluationTemplateModalVisible}
                onClose={() => setEvaluationTemplateModalVisible(false)}
                size="lg"
            />
        </>
    );
};

export default ExaminersSection;
